import {get} from 'lodash/fp';
import {createSelector} from 'reselect';

import {
  COUNTRY_OPTIONS,
  REGISTERED_FIELDS,
  US_STATES,
} from '/component/screen/Profile/ProfileBusinessSection/ManageMySite/ManageMySite.constants';
import {LEADS_POOL_OPT_IN_REQUIREMENTS} from '/component/screen/Profile/ProfileBusinessSection/ProfileBusinessSection.constants';
import {
  REMOTE_CONFIG_BANNER_TEXT,
  REMOTE_CONFIG_BONUS_DISPLAY_DATES,
  REMOTE_CONFIG_BUILD_NUMBER,
  REMOTE_CONFIG_CHAT_BETA_USER_EMAILS,
  REMOTE_CONFIG_CHAT_CODE_CONFIGS,
  REMOTE_CONFIG_CODE_CONFIGS,
  REMOTE_CONFIG_COUNTRIES,
  REMOTE_CONFIG_EDGE30_TEXT,
  REMOTE_CONFIG_FEATURE_FLAG,
  REMOTE_CONFIG_HEALTH_CARE_PROVIDER_FIELDS,
  REMOTE_CONFIG_LAST_FORCED_BUILD_NUMBER,
  REMOTE_CONFIG_LEADS_POOL_OPT_IN_REQUIREMENTS,
  REMOTE_CONFIG_MAINTENANCE,
  REMOTE_CONFIG_MEAL_PLAN,
  REMOTE_CONFIG_RELEASE_NOTES,
  REMOTE_CONFIG_UPDATE_NOTES,
  REMOTE_CONFIG_UPDATE_TYPE,
  REMOTE_CONFIG_US_STATES,
} from '/enum/remoteConfig.enum';

import {remoteConfig as remoteConfigState} from './root.selector';

const parseRemoteArray = (value: string, fallback: any[]) => {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

const parseRemoteObject = <T extends object>(value: string, fallback: T): T => {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : fallback;
  } catch {
    return fallback;
  }
};

export interface LeadsPoolOptInRequirements {
  optInRequirementText: string;
  qualifiedNewClientDefinitionText: string;
}

export const remoteConfigVariables = createSelector(
  remoteConfigState,
  get('variables'),
);

export const variablesBuildNumber = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_BUILD_NUMBER),
);

export const variablesUpdateType = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_UPDATE_TYPE),
);

export const variablesLastForcedBuildNumber = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_LAST_FORCED_BUILD_NUMBER),
);

export const variablesUpdateNotes = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_UPDATE_NOTES),
);

export const variablesReleaseNotes = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_RELEASE_NOTES),
);

export const variablesMaintenance = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_MAINTENANCE),
);

export const variablesFeatureFlag = createSelector(
  remoteConfigVariables,
  variables => {
    const featureFlagValue = get(REMOTE_CONFIG_FEATURE_FLAG, variables) as any;
    if (featureFlagValue && typeof featureFlagValue.asString === 'function') {
      try {
        return JSON.parse(featureFlagValue.asString());
      } catch {
        return {};
      }
    }
    if (
      featureFlagValue &&
      typeof featureFlagValue === 'object' &&
      !Array.isArray(featureFlagValue)
    ) {
      return featureFlagValue;
    }
    return {};
  },
);

export const variablesCodeConfigs = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_CODE_CONFIGS),
);

export const variablesChatCodeConfigs = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_CHAT_CODE_CONFIGS),
);

export const variablesChatBetaUserEmails = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_CHAT_BETA_USER_EMAILS),
);

export const variablesBannerText = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_BANNER_TEXT),
);

export const variablesEdge30Text = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_EDGE30_TEXT),
);
export const variablesMealPlan = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_MEAL_PLAN),
);

export const variablesBonusDisplayDate = createSelector(
  remoteConfigVariables,
  variables => {
    const bonusDisplayDateValue = get(
      REMOTE_CONFIG_BONUS_DISPLAY_DATES,
      variables,
    ) as any;
    if (
      bonusDisplayDateValue &&
      typeof bonusDisplayDateValue.asString === 'function'
    ) {
      try {
        return JSON.parse(bonusDisplayDateValue.asString());
      } catch {
        return {};
      }
    }
    if (bonusDisplayDateValue) {
      return bonusDisplayDateValue;
    }
    return {};
  },
);

export const variablesEdge365Tasks = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_EDGE30_TEXT),
);

export interface ChatQuickSuggestion {
  label: string;
  query: string;
}

export const selectChatQuickSuggestions = createSelector(
  variablesChatCodeConfigs,
  (codeConfigs): ChatQuickSuggestion[] => {
    const suggestions = codeConfigs?.chat_quick_suggestions;
    if (!Array.isArray(suggestions) || suggestions.length === 0) {
      return [];
    }
    return suggestions.filter(
      (s: any): s is ChatQuickSuggestion =>
        s != null && typeof s.label === 'string' && typeof s.query === 'string',
    );
  },
);

export const variablesHealthCareProviderFields = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_HEALTH_CARE_PROVIDER_FIELDS),
);

export const variablesUsStates = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_US_STATES),
);

export const variablesCountries = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_COUNTRIES),
);

export const variablesLeadsPoolOptInRequirements = createSelector(
  remoteConfigVariables,
  get(REMOTE_CONFIG_LEADS_POOL_OPT_IN_REQUIREMENTS),
);

export const selectHealthCareProviderFields = createSelector(
  variablesHealthCareProviderFields,
  fields => {
    const parsed = parseRemoteArray(fields, REGISTERED_FIELDS);
    return [...parsed].sort((a, b) => {
      if (a.value === 'other') return 1;
      if (b.value === 'other') return -1;
      return a.label?.localeCompare(b.label);
    });
  },
);

export const selectUsStates = createSelector(variablesUsStates, states => {
  const parsed = parseRemoteArray(states, US_STATES);
  return [...parsed].sort((a, b) => a.label?.localeCompare(b.label));
});

export const selectCountries = createSelector(variablesCountries, countries => {
  const parsed = parseRemoteArray(countries, COUNTRY_OPTIONS);
  return [...parsed].sort((a, b) => a.label?.localeCompare(b.label));
});

export const selectLeadsPoolOptInRequirements = createSelector(
  variablesLeadsPoolOptInRequirements,
  requirements => {
    return parseRemoteObject(requirements, LEADS_POOL_OPT_IN_REQUIREMENTS);
  },
);
